import React from "react";
import type { Theme } from "../lib/theme";
import type { RFIItem, Division, ItemState } from "../types";
import { walkItems } from "../lib/calculations";
import * as Icon from "../components/OEIcons";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

interface RfiTabProps {
  T: Theme;
  rfis: RFIItem[];
  setRfis: React.Dispatch<React.SetStateAction<RFIItem[]>>;
  rfiSearch: string;
  setRfiSearch: React.Dispatch<React.SetStateAction<string>>;
  rfiStatusFilter: string;
  setRfiStatusFilter: React.Dispatch<React.SetStateAction<any>>;
  activeRfiId: string | null;
  setActiveRfiId: React.Dispatch<React.SetStateAction<string | null>>;
  rfiItemPickerFor: string | null;
  setRfiItemPickerFor: React.Dispatch<React.SetStateAction<string | null>>;
  rfiItemSearch: string;
  setRfiItemSearch: React.Dispatch<React.SetStateAction<string>>;
  rfiAssigneeOptions: { value: string; hint: string }[];
  setRfiJumpHighlight: React.Dispatch<React.SetStateAction<string | null>>;
  divisions: Division[];
  itemState: Record<string, ItemState>;
  snapshot: () => void;
  uid: () => string;
  fmt: (n: number) => string;
  setActiveTab: (tab: string) => void;
  /** Opens the Procore RFI export. Absent when there is nothing to export to. */
  onExportProcore?: () => void;
}

export default function RfiTab({
  T, rfis, setRfis, rfiSearch, setRfiSearch, rfiStatusFilter, setRfiStatusFilter,
  activeRfiId, setActiveRfiId, rfiItemPickerFor, setRfiItemPickerFor,
  rfiItemSearch, setRfiItemSearch, rfiAssigneeOptions, setRfiJumpHighlight,
  divisions, itemState, snapshot, uid, fmt, setActiveTab, onExportProcore,
}: RfiTabProps) {
  const filteredRfis = rfis.filter(r => {
    const matchSearch = !rfiSearch || r.subject.toLowerCase().includes(rfiSearch.toLowerCase()) || String(r.number).includes(rfiSearch) || r.assignedTo.toLowerCase().includes(rfiSearch.toLowerCase());
    if (!matchSearch) return false;
    if (rfiStatusFilter === "all") return true;
    if (rfiStatusFilter === "overdue") return r.status === "open" && !!r.dueDate && new Date(r.dueDate + "T00:00:00") < new Date();
    return r.status === rfiStatusFilter;
  });
  const activeRfi = rfis.find(r => r.id === activeRfiId) || null;
  const openCt = rfis.filter(r => r.status === "open").length;
  const answeredCt = rfis.filter(r => r.status === "answered").length;
  const closedCt = rfis.filter(r => r.status === "closed").length;
  const voidCt = rfis.filter(r => r.status === "void").length;
  const overdueCt = rfis.filter(r => r.status === "open" && r.dueDate && new Date(r.dueDate + "T00:00:00") < new Date()).length;

  const addRfi = () => {
    snapshot();
    const n: RFIItem = {
      id: uid(), number: rfis.length > 0 ? Math.max(...rfis.map(r => r.number)) + 1 : 1,
      subject: "", status: "open", priority: "normal", submittedBy: "", assignedTo: "",
      question: "", response: "", drawingRef: "", specSection: "",
      createdAt: new Date().toISOString().split("T")[0], dueDate: "", answeredAt: "",
      linkedItemIds: [], costImpact: 0, scheduleImpact: 0,
    };
    setRfis(prev => [...prev, n]);
    setActiveRfiId(n.id);
  };
  const updateRfi = (id: string, patch: Partial<RFIItem>) => setRfis(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));
  const deleteRfi = (id: string) => { snapshot(); setRfis(prev => prev.filter(r => r.id !== id)); if (activeRfiId === id) setActiveRfiId(null); };

  const allEstItems: { id: string; divName: string; desc: string; total: number }[] = [];
  divisions.forEach(div => {
    const divShort = (div.name || div.id).replace(/^Division \d+ [–-] /, "").replace(/^Div \d+ [–-] /, "");
    (div.subcategories || []).forEach(sub => {
      [...walkItems(sub.rows || [])].forEach(item => {
        const li = itemState[item.id];
        if (!li || !li.included) return;
        allEstItems.push({ id: item.id, divName: divShort, desc: li.description || "—", total: (li.qty || 0) * (li.unitCost || 0) });
      });
    });
  });
  const pickerItems = allEstItems.filter(i => !rfiItemSearch || i.desc.toLowerCase().includes(rfiItemSearch.toLowerCase()) || i.divName.toLowerCase().includes(rfiItemSearch.toLowerCase()));

  const STATUS_COLOR: Record<string, string> = { open: T.borderAccent, answered: T.positive, closed: T.textMuted, void: T.textMuted };
  const STATUS_BG: Record<string, string> = { open: `${T.borderAccent}18`, answered: `${T.positive}18`, closed: `${T.borderPrimary}44`, void: `${T.borderPrimary}44` };
  const PRI_COLOR: Record<string, string> = { normal: T.textMuted, high: "#e8a04a", urgent: T.negative };

  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden", fontFamily: FONT }}>
      {/* List panel */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>
        {/* Toolbar */}
        <div style={{ padding: "14px 20px 10px", borderBottom: `1px solid ${T.borderPrimary}`, flexShrink: 0, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" as any }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.borderAccent, flex: 1, minWidth: 120 }}>RFIs</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, background: T.bgApp, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, padding: "4px 10px" }}>
            <Icon.Search size={11} color={T.textMuted} />
            <input value={rfiSearch} onChange={e => setRfiSearch(e.target.value)} placeholder="Search RFIs…"
              style={{ background: "none", border: "none", outline: "none", color: T.textSecondary, fontSize: 11, fontFamily: FONT, width: 160 }} />
          </div>
          {/* OpenEstimate holds the preconstruction decision record; Procore
              owns the RFI process once construction starts. Procore has no bulk
              import for RFIs, so this is the only way that history travels. */}
          {onExportProcore && rfis.length > 0 && (
            <button onClick={onExportProcore} title="Send these RFIs to a Procore project as a permanent record"
              style={{ background: "none", border: `1px solid ${T.borderSecondary}`, color: T.textMuted, cursor: "pointer", padding: "7px 14px", borderRadius: 6, fontSize: 12, fontFamily: FONT, display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" as any }}>
              <Icon.Upload size={11} /> To Procore
            </button>
          )}
          <button onClick={addRfi}
            style={{ background: T.borderAccent, border: "none", color: T.textOnGold, cursor: "pointer", padding: "7px 16px", borderRadius: 6, fontSize: 12, fontFamily: FONT, fontWeight: 700, display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" as any }}>
            <Icon.Plus size={11} /> New RFI
          </button>
        </div>
        {/* Stats bar */}
        <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${T.borderPrimary}`, flexShrink: 0 }}>
          {[
            { label: "Open", count: openCt, filter: "open" as const, color: T.borderAccent },
            { label: "Answered", count: answeredCt, filter: "answered" as const, color: T.positive },
            { label: "Closed", count: closedCt, filter: "closed" as const, color: T.textMuted },
            { label: "Overdue", count: overdueCt, filter: "overdue" as const, color: T.negative },
            { label: "Void", count: voidCt, filter: "void" as const, color: T.textWeak },
            { label: "All", count: rfis.length, filter: "all" as const, color: T.textWeak },
          ].map(({ label, count, filter, color }) => (
            <button key={label} onClick={() => setRfiStatusFilter(filter)}
              style={{ flex: 1, background: rfiStatusFilter === filter ? `${T.borderAccent}10` : "none",
                border: "none", borderBottom: rfiStatusFilter === filter ? `2px solid ${T.borderAccent}` : "2px solid transparent",
                padding: "10px 8px", cursor: "pointer", fontFamily: FONT, textAlign: "center" as any,
                opacity: count === 0 && label !== "All" ? 0.5 : 1 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: count > 0 && label === "Overdue" ? T.negative : color }}>{count}</div>
              <div style={{ fontSize: 10, color: T.textMuted, letterSpacing: ".08em", textTransform: "uppercase" as any, marginTop: 1 }}>{label}</div>
            </button>
          ))}
        </div>

        {/* Table */}
        <div style={{ flex: 1, overflowY: "auto" as any }}>
          {filteredRfis.length === 0 ? (
            <div style={{ padding: "48px 24px", textAlign: "center" as any, color: T.textMuted }}>
              <div style={{ marginBottom: 12, opacity: .25 }}><Icon.Clipboard size={32} /></div>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.textWeak, marginBottom: 6 }}>{rfis.length === 0 ? "No RFIs yet" : "No matching RFIs"}</div>
              <div style={{ fontSize: 11 }}>{rfis.length === 0 ? "Click New RFI to log your first question to the design team." : "Try a different filter or search."}</div>
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" as any, fontSize: 11 }}>
              <thead>
                <tr style={{ background: T.bgPanel, borderBottom: `1px solid ${T.borderPrimary}` }}>
                  {["#", "Subject", "Assigned To", "Status", "Due Date", "Cost Impact", "Items"].map(h => (
                    <th key={h} style={{ padding: "7px 12px", textAlign: "left" as any, fontSize: 10, fontWeight: 700, color: T.textMuted, letterSpacing: ".08em", textTransform: "uppercase" as any, whiteSpace: "nowrap" as any }}>{h}</th>
                  ))}
                  <th style={{ width: 32 }} />
                </tr>
              </thead>
              <tbody>
                {filteredRfis.map(rfi => {
                  const isActive = rfi.id === activeRfiId;
                  const isOverdue = rfi.status === "open" && rfi.dueDate && new Date(rfi.dueDate + "T00:00:00") < new Date();
                  return (
                    <tr key={rfi.id} onClick={() => setActiveRfiId(isActive ? null : rfi.id)}
                      style={{ borderBottom: `1px solid ${T.borderPrimary}22`, cursor: "pointer", background: isActive ? `${T.borderAccent}0e` : "transparent", transition: "background .1s" }}
                      onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = `${T.borderAccent}07`; }}
                      onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = "transparent"; }}>
                      <td style={{ padding: "9px 12px", color: T.textMuted, fontWeight: 600, whiteSpace: "nowrap" as any }}>RFI-{String(rfi.number).padStart(3, "0")}</td>
                      <td style={{ padding: "9px 12px", color: T.textSecondary, maxWidth: 260 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          {rfi.priority !== "normal" && <span style={{ width: 6, height: 6, borderRadius: "50%", background: PRI_COLOR[rfi.priority], flexShrink: 0, display: "inline-block" }} />}
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as any }}>{rfi.subject || <span style={{ color: T.textMuted, fontStyle: "italic" }}>Untitled</span>}</span>
                        </div>
                        {rfi.drawingRef && <div style={{ fontSize: 10, color: T.textMuted, marginTop: 2 }}>Ref: {rfi.drawingRef}</div>}
                      </td>
                      <td style={{ padding: "9px 12px", color: T.textTertiary, whiteSpace: "nowrap" as any }}>{rfi.assignedTo || "—"}</td>
                      <td style={{ padding: "9px 12px", whiteSpace: "nowrap" as any }}>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 3, letterSpacing: ".04em", textTransform: "uppercase" as any,
                          background: STATUS_BG[rfi.status] || STATUS_BG.open, color: STATUS_COLOR[rfi.status] || STATUS_COLOR.open }}>
                          {rfi.status}
                        </span>
                      </td>
                      <td style={{ padding: "9px 12px", color: isOverdue ? T.negative : T.textTertiary, whiteSpace: "nowrap" as any }}>
                        {rfi.dueDate ? new Date(rfi.dueDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" } as any) : "—"}
                        {isOverdue && <span style={{ fontSize: 10, color: T.negative, marginLeft: 4 }}>Overdue</span>}
                      </td>
                      <td style={{ padding: "9px 12px", color: rfi.costImpact ? T.negative : T.textMuted, textAlign: "right" as any, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" as any }}>
                        {rfi.costImpact ? fmt(rfi.costImpact) : "—"}
                      </td>
                      <td style={{ padding: "9px 12px", color: T.textMuted, textAlign: "right" as any }}>
                        {rfi.linkedItemIds.length > 0 ? <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 3, background: `${T.borderAccent}18`, color: T.borderAccent, fontWeight: 600 }}>{rfi.linkedItemIds.length}</span> : "—"}
                      </td>
                      <td style={{ padding: "9px 8px", textAlign: "center" as any }}>
                        <button onClick={e => { e.stopPropagation(); deleteRfi(rfi.id); }} title="Delete RFI"
                          style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", padding: "2px 4px", opacity: .5, lineHeight: 1, display: "flex", alignItems: "center" }}
                          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = T.negative; (e.currentTarget as HTMLElement).style.opacity = "1"; }}
                          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = T.textMuted; (e.currentTarget as HTMLElement).style.opacity = ".5"; }}>
                          <Icon.X size={11} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {(() => {
                let pending = 0, resolved = 0;
                filteredRfis.forEach(r => {
                  if (r.status === "void") return;
                  const v = r.costImpact || 0;
                  if (r.status === "closed") resolved += v;
                  else if (r.status === "open" || r.status === "answered") pending += v;
                });
                const total = pending + resolved;
                if (pending === 0 && resolved === 0) return null;
                const fmtSigned = (v: number) => v === 0 ? "$0" : (v > 0 ? "+" : "") + fmt(v);
                const colorFor = (v: number) => v === 0 ? T.textMuted : v > 0 ? T.negative : T.positive;
                return (
                  <tfoot>
                    <tr style={{ borderTop: `1px solid ${T.borderPrimary}`, background: T.bgPanel }}>
                      <td colSpan={5} style={{ padding: "10px 12px", fontSize: 10, color: T.textWeak, letterSpacing: ".08em", textTransform: "uppercase" as any, fontWeight: 700, whiteSpace: "nowrap" as any }}>
                        Cost Impact
                        <span style={{ marginLeft: 14, textTransform: "none" as any, letterSpacing: 0, fontWeight: 500, color: T.textMuted }}>
                          Pending <span title="Open + answered RFIs (not yet final)" style={{ marginLeft: 4, fontWeight: 700, color: colorFor(pending), fontVariantNumeric: "tabular-nums" }}>{fmtSigned(pending)}</span>
                        </span>
                        <span style={{ marginLeft: 14, textTransform: "none" as any, letterSpacing: 0, fontWeight: 500, color: T.textMuted }}>
                          Resolved <span title="Closed RFIs (locked into estimate)" style={{ marginLeft: 4, fontWeight: 700, color: colorFor(resolved), fontVariantNumeric: "tabular-nums" }}>{fmtSigned(resolved)}</span>
                        </span>
                      </td>
                      <td style={{ padding: "10px 12px", textAlign: "right" as any, fontVariantNumeric: "tabular-nums", fontWeight: 700, color: colorFor(total), whiteSpace: "nowrap" as any }}
                        title="Total cost impact across the rows shown above">
                        {fmtSigned(total)}
                      </td>
                      <td /><td />
                    </tr>
                  </tfoot>
                );
              })()}
            </table>
          )}
        </div>
      </div>

      {/* Detail panel */}
      {activeRfi && (
        <div style={{ width: 460, borderLeft: `1px solid ${T.borderPrimary}`, display: "flex", flexDirection: "column", overflow: "hidden", flexShrink: 0, background: T.bgPanel }}>
          <div style={{ padding: "14px 18px 12px", borderBottom: `1px solid ${T.borderPrimary}`, flexShrink: 0, display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 2 }}>RFI-{String(activeRfi.number).padStart(3, "0")}</div>
              <input value={activeRfi.subject} onChange={e => { snapshot(); updateRfi(activeRfi.id, { subject: e.target.value }); }}
                placeholder="RFI subject / question title…"
                style={{ width: "100%", background: "none", border: "none", outline: "none", fontSize: 14, fontWeight: 700, color: T.textSecondary, fontFamily: FONT, padding: 0, boxSizing: "border-box" as any }} />
            </div>
            <button onClick={() => setActiveRfiId(null)} style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", padding: "2px 4px", flexShrink: 0, display: "flex" }}>
              <Icon.X size={14} />
            </button>
          </div>
          <div style={{ flex: 1, overflowY: "auto" as any, padding: "16px 18px", display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Meta grid */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {([
                { label: "Status", field: "status", type: "select", opts: ["open", "answered", "closed", "void"] },
                { label: "Priority", field: "priority", type: "select", opts: ["normal", "high", "urgent"] },
                { label: "Submitted By", field: "submittedBy", type: "text" },
                { label: "Assigned To", field: "assignedTo", type: "text" },
                { label: "Date Created", field: "createdAt", type: "date" },
                { label: "Due Date", field: "dueDate", type: "date" },
                { label: "Drawing Ref", field: "drawingRef", type: "text" },
                { label: "Spec Section", field: "specSection", type: "text" },
              ] as const).map(({ label, field, type, opts }: any) => (
                <div key={field}>
                  <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".08em", textTransform: "uppercase" as any, marginBottom: 4 }}>{label}</div>
                  {type === "select" ? (
                    <select value={(activeRfi as any)[field]} onChange={e => {
                      const patch: Partial<RFIItem> = { [field]: e.target.value };
                      if (field === "status" && e.target.value === "answered" && !activeRfi.answeredAt) patch.answeredAt = new Date().toISOString().split("T")[0];
                      snapshot(); updateRfi(activeRfi.id, patch);
                    }}
                      style={{ width: "100%", background: T.bgApp, border: `1px solid ${T.borderSecondary}`, color: T.textSecondary, padding: "5px 8px", borderRadius: 3, fontSize: 11, fontFamily: FONT, outline: "none" }}>
                      {(opts || []).map((o: string) => <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>)}
                    </select>
                  ) : (
                    <>
                      <input type={type} value={(activeRfi as any)[field] || ""}
                        onChange={e => { snapshot(); updateRfi(activeRfi.id, { [field]: e.target.value }); }}
                        list={field === "assignedTo" ? "rfi-assignee-list" : undefined}
                        placeholder={field === "assignedTo" ? "Name or company…" : undefined}
                        style={{ width: "100%", background: T.bgApp, border: `1px solid ${T.borderSecondary}`, color: T.textSecondary, padding: "5px 8px", borderRadius: 3, fontSize: 11, fontFamily: FONT, outline: "none", boxSizing: "border-box" as any }} />
                      {field === "assignedTo" && (
                        <datalist id="rfi-assignee-list">
                          {rfiAssigneeOptions.map((o, i) => <option key={i} value={o.value}>{o.hint}</option>)}
                        </datalist>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
            {/* Cost / Schedule impact */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div>
                <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".08em", textTransform: "uppercase" as any, marginBottom: 4 }}>Cost Impact ($)</div>
                <input type="number" value={activeRfi.costImpact || ""} placeholder="0"
                  onChange={e => { snapshot(); updateRfi(activeRfi.id, { costImpact: parseFloat(e.target.value) || 0 }); }}
                  style={{ width: "100%", background: T.bgApp, border: `1px solid ${T.borderSecondary}`, color: T.textSecondary, padding: "5px 8px", borderRadius: 3, fontSize: 11, fontFamily: FONT, outline: "none", boxSizing: "border-box" as any }} />
              </div>
              <div>
                <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".08em", textTransform: "uppercase" as any, marginBottom: 4 }}>Schedule Impact (days)</div>
                <input type="number" value={activeRfi.scheduleImpact || ""} placeholder="0"
                  onChange={e => { snapshot(); updateRfi(activeRfi.id, { scheduleImpact: parseInt(e.target.value) || 0 }); }}
                  style={{ width: "100%", background: T.bgApp, border: `1px solid ${T.borderSecondary}`, color: T.textSecondary, padding: "5px 8px", borderRadius: 3, fontSize: 11, fontFamily: FONT, outline: "none", boxSizing: "border-box" as any }} />
              </div>
            </div>
            {/* Question */}
            <div>
              <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".08em", textTransform: "uppercase" as any, marginBottom: 6 }}>Question</div>
              <textarea value={activeRfi.question} rows={4}
                onChange={e => { snapshot(); updateRfi(activeRfi.id, { question: e.target.value }); }}
                placeholder="Describe the question or clarification needed…"
                style={{ width: "100%", background: T.bgApp, border: `1px solid ${T.borderSecondary}`, color: T.textSecondary, padding: "8px 10px", borderRadius: 3, fontSize: 11, fontFamily: FONT, outline: "none", resize: "vertical" as any, lineHeight: 1.55, boxSizing: "border-box" as any }} />
            </div>
            {/* Response */}
            <div>
              <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".08em", textTransform: "uppercase" as any, marginBottom: 6 }}>
                Response
                {activeRfi.answeredAt && <span style={{ marginLeft: 8, fontSize: 10, color: T.positive, fontWeight: 600 }}>Received {new Date(activeRfi.answeredAt + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" } as any)}</span>}
              </div>
              <textarea value={activeRfi.response} rows={4}
                onChange={e => { snapshot(); updateRfi(activeRfi.id, { response: e.target.value, status: e.target.value && activeRfi.status === "open" ? "answered" : activeRfi.status, answeredAt: e.target.value && !activeRfi.answeredAt ? new Date().toISOString().split("T")[0] : activeRfi.answeredAt }); }}
                placeholder="Enter formal response from design team or owner…"
                style={{ width: "100%", background: T.bgApp, border: `1px solid ${T.borderSecondary}`, color: T.textSecondary, padding: "8px 10px", borderRadius: 3, fontSize: 11, fontFamily: FONT, outline: "none", resize: "vertical" as any, lineHeight: 1.55, boxSizing: "border-box" as any }} />
            </div>
            {/* Linked estimate items */}
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".08em", textTransform: "uppercase" as any, flex: 1 }}>Linked Estimate Items</div>
                <button onClick={() => { setRfiItemPickerFor(activeRfi.id); setRfiItemSearch(""); }}
                  style={{ fontSize: 10, padding: "3px 10px", borderRadius: 3, cursor: "pointer", fontFamily: FONT, fontWeight: 600,
                    border: `1px solid ${T.borderAccentFaint}`, background: `${T.borderAccent}12`, color: T.borderAccent, display: "flex", alignItems: "center", gap: 4 }}>
                  <Icon.Plus size={9} /> Link Item
                </button>
              </div>
              {activeRfi.linkedItemIds.length === 0 ? (
                <div style={{ fontSize: 11, color: T.textMuted, fontStyle: "italic", padding: "10px 0" }}>No estimate items linked yet. Click Link Item to connect this RFI to one or more line items.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column" as any, gap: 4 }}>
                  {activeRfi.linkedItemIds.map(iid => {
                    const li = itemState[iid];
                    const snap = activeRfi.linkedItemSnapshots?.[iid];
                    const exists = !!li;
                    const div = exists ? divisions.find(d => d.subcategories.some(s => [...walkItems(s.rows || [])].some(r => r.id === iid))) : null;
                    const divShort = (div?.name || snap?.divLabel || "").replace(/^Division \d+ [–-] /, "").replace(/^Div \d+ [–-] /, "");
                    const desc = exists ? (li.description || "—") : (snap?.description || "(item removed)");
                    const total = exists ? ((li.qty || 0) * (li.unitCost || 0)) : (snap?.total || 0);
                    return (
                      <div key={iid} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderRadius: 3,
                        background: T.bgApp, border: `1px solid ${exists ? T.borderPrimary : T.negative + "55"}`, opacity: exists ? 1 : .7 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 11, color: T.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as any }}>
                            {desc}{!exists && <span style={{ color: T.negative, marginLeft: 6, fontSize: 10 }}>(deleted)</span>}
                          </div>
                          <div style={{ fontSize: 10, color: T.textMuted, marginTop: 1 }}>{divShort} · {fmt(total)}</div>
                        </div>
                        {exists && (
                          <button onClick={() => {
                            setActiveTab("estimate");
                            setRfiJumpHighlight(iid);
                            setTimeout(() => {
                              const el = document.querySelector(`[data-itemid="${iid}"]`) as HTMLElement | null;
                              if (el) {
                                el.scrollIntoView({ behavior: "smooth", block: "center" });
                                el.style.transition = "background .3s";
                                const orig = el.style.background;
                                el.style.background = `${T.borderAccent}55`;
                                setTimeout(() => { el.style.background = orig; setRfiJumpHighlight(null); }, 1600);
                              }
                            }, 120);
                          }}
                            title="Jump to estimate line"
                            style={{ background: "none", border: `1px solid ${T.borderAccentFaint}`, color: T.borderAccent, cursor: "pointer", padding: "3px 8px", fontSize: 10, fontWeight: 600, borderRadius: 3, fontFamily: FONT, flexShrink: 0 }}>
                            Jump →
                          </button>
                        )}
                        <button onClick={() => { snapshot(); updateRfi(activeRfi.id, { linkedItemIds: activeRfi.linkedItemIds.filter(x => x !== iid) }); }}
                          title="Unlink this item"
                          style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", padding: "2px 3px", flexShrink: 0, display: "flex", opacity: .6 }}
                          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = T.negative; (e.currentTarget as HTMLElement).style.opacity = "1"; }}
                          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = T.textMuted; (e.currentTarget as HTMLElement).style.opacity = ".6"; }}>
                          <Icon.X size={11} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Item picker modal */}
      {rfiItemPickerFor && (
        <div style={{ position: "fixed", inset: 0, zIndex: 400, background: "rgba(0,0,0,.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
          onClick={() => setRfiItemPickerFor(null)}>
          <div style={{ background: T.bgPanel, borderRadius: 6, width: "100%", maxWidth: 520, maxHeight: "70vh", display: "flex", flexDirection: "column" as any, overflow: "hidden" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding: "16px 20px 12px", borderBottom: `1px solid ${T.borderPrimary}`, display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.borderAccent, flex: 1 }}>Link Estimate Item</div>
              <button onClick={() => setRfiItemPickerFor(null)} style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", display: "flex" }}><Icon.X size={14} /></button>
            </div>
            <div style={{ padding: "10px 16px", borderBottom: `1px solid ${T.borderPrimary}`, flexShrink: 0 }}>
              <input value={rfiItemSearch} onChange={e => setRfiItemSearch(e.target.value)} placeholder="Search items…" autoFocus
                style={{ width: "100%", background: T.bgApp, border: `1px solid ${T.borderSecondary}`, color: T.textSecondary, padding: "6px 10px", borderRadius: 3, fontSize: 11, fontFamily: FONT, outline: "none", boxSizing: "border-box" as any }} />
            </div>
            <div style={{ flex: 1, overflowY: "auto" as any }}>
              {pickerItems.slice(0, 120).map(item => {
                const rfi = rfis.find(r => r.id === rfiItemPickerFor);
                const isLinked = rfi?.linkedItemIds.includes(item.id) || false;
                return (
                  <div key={item.id}
                    onClick={() => {
                      if (!rfi) return;
                      snapshot();
                      if (isLinked) {
                        const nextSnaps = { ...(rfi.linkedItemSnapshots || {}) };
                        delete nextSnaps[item.id];
                        updateRfi(rfi.id, { linkedItemIds: rfi.linkedItemIds.filter(x => x !== item.id), linkedItemSnapshots: nextSnaps });
                      } else {
                        const li = itemState[item.id] || {};
                        const div = divisions.find(d => d.subcategories.some((s: any) => [...walkItems(s.rows || [])].some((r: any) => r.id === item.id)));
                        const sub = div?.subcategories?.find((s: any) => [...walkItems(s.rows || [])].some((r: any) => r.id === item.id));
                        const divShort = (div?.name || "").replace(/^Division \d+ [–-] /, "").replace(/^Div \d+ [–-] /, "");
                        const snap = { description: li.description || "", divLabel: divShort, subLabel: sub?.name || "", qty: li.qty || 0, unit: li.unit || "", unitCost: li.unitCost || 0, total: (li.qty || 0) * (li.unitCost || 0), capturedAt: new Date().toISOString() };
                        updateRfi(rfi.id, { linkedItemIds: [...rfi.linkedItemIds, item.id], linkedItemSnapshots: { ...(rfi.linkedItemSnapshots || {}), [item.id]: snap } });
                      }
                    }}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 16px", cursor: "pointer", borderBottom: `1px solid ${T.borderPrimary}18`, background: isLinked ? `${T.borderAccent}0e` : "transparent", transition: "background .1s" }}
                    onMouseEnter={e => { if (!isLinked) (e.currentTarget as HTMLElement).style.background = `${T.borderAccent}07`; }}
                    onMouseLeave={e => { if (!isLinked) (e.currentTarget as HTMLElement).style.background = "transparent"; }}>
                    <div style={{ width: 16, height: 16, borderRadius: 3, border: `1.5px solid ${isLinked ? T.borderAccent : T.borderSecondary}`, background: isLinked ? T.borderAccent : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {isLinked && <Icon.Check size={10} color={T.textOnGold} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, color: T.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as any }}>{item.desc}</div>
                      <div style={{ fontSize: 10, color: T.textMuted, marginTop: 1 }}>{item.divName} · {fmt(item.total)}</div>
                    </div>
                  </div>
                );
              })}
              {pickerItems.length === 0 && <div style={{ padding: "32px 16px", textAlign: "center" as any, color: T.textMuted, fontSize: 11 }}>No items match your search.</div>}
            </div>
            <div style={{ padding: "12px 16px", borderTop: `1px solid ${T.borderPrimary}`, flexShrink: 0, textAlign: "right" as any }}>
              <button onClick={() => setRfiItemPickerFor(null)}
                style={{ background: T.borderAccent, border: "none", color: T.textOnGold, cursor: "pointer", padding: "7px 20px", borderRadius: 3, fontSize: 12, fontFamily: FONT, fontWeight: 600 }}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
